import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { specLabel } from "@/lib/specialisms";
import { toast } from "sonner";
import { Trash2, ChevronDown, ChevronUp, Instagram, Music2, Youtube, Pencil, Save, X } from "lucide-react";

const STATUS_META = {
    incomplete: { label: "Incomplete", cls: "bg-white" },
    pending: { label: "Pending", cls: "bg-yc-cream" },
    more_info: { label: "More info", cls: "bg-yc-pink text-white" },
    approved: { label: "Accepted", cls: "bg-yc-blue text-white" },
    rejected: { label: "Rejected", cls: "bg-yc-ink text-white" },
};

const AdminCreators = () => {
    const [creators, setCreators] = useState([]);
    const [filter, setFilter] = useState("all");
    const [open, setOpen] = useState(null);
    const [editing, setEditing] = useState(null);
    const [eform, setEform] = useState({});
    const [savingEdit, setSavingEdit] = useState(false);

    const load = () => api.get("/admin/creators").then((r) => setCreators(r.data));
    useEffect(() => { load(); }, []);

    const EDIT_FIELDS = [
        ["name", "Display name"], ["first_name", "First name"], ["surname", "Surname"],
        ["contact_email", "Contact email"], ["telephone", "Telephone"], ["address", "Address"],
        ["bank_account_name", "Account holder"], ["bank_sort_code", "Sort code"], ["bank_account_number", "Account number"],
    ];
    const startEdit = (c) => {
        setEditing(c.creator_id);
        setEform(EDIT_FIELDS.reduce((a, [k]) => ({ ...a, [k]: c[k] || "" }), {}));
    };
    const saveEdit = async (creator_id) => {
        setSavingEdit(true);
        try {
            await api.put(`/admin/creator/${creator_id}`, eform);
            toast.success("Account details updated");
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save changes");
        } finally { setSavingEdit(false); }
    };

    const remove = async (c) => {
        if (!window.confirm(`Permanently remove ${c.name || c.login_email || "this creator"} from the platform? This deletes their account, profile, messages and work records. This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/creator/${c.creator_id}`);
            toast.success("Creator removed from the platform");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not remove creator");
        }
    };

    const shown = filter === "all" ? creators : creators.filter((c) => c.vetting_status === filter);
    const counts = creators.reduce((a, c) => ({ ...a, [c.vetting_status]: (a[c.vetting_status] || 0) + 1 }), {});
    const poolCount = creators.filter((c) => c.is_seed_pool).length;

    const seedPool = async () => {
        if (!window.confirm("Seed the roster with believable creator profiles (up to 202 total)? Brands will see them in the network.")) return;
        try {
            const r = await api.post("/admin/creator-pool");
            toast.success(`Pool ready — ${r.data.pool_total} seeded creators (${r.data.created} new)`);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not seed pool"); }
    };
    const removePool = async () => {
        if (!window.confirm("Remove ALL seeded pool creators from the roster?")) return;
        try {
            const r = await api.delete("/admin/creator-pool");
            toast.success(`${r.data.removed} seeded creators removed`);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not remove pool"); }
    };

    return (
        <DashboardShell role="admin" title="Creators" subtitle={`Every creator on the platform · ${creators.length} total`}>
            <div className="yc-card-sm p-4 flex flex-wrap items-center gap-3" data-testid="pool-controls">
                <div className="flex-1 min-w-[220px]">
                    <div className="font-heading font-bold text-sm">Seeded creator pool</div>
                    <div className="text-xs text-yc-ink/60">{poolCount} seeded profiles live on the roster — they make the network feel busy for new brands and creators.</div>
                </div>
                <button data-testid="seed-pool-btn" onClick={seedPool} className="yc-btn-primary text-xs py-2 px-4">Seed pool (202)</button>
                <button data-testid="remove-pool-btn" onClick={removePool} className="yc-btn-secondary text-xs py-2 px-4">Remove pool</button>
            </div>
            <div className="flex flex-wrap gap-2">
                {["all", "approved", "pending", "more_info", "rejected", "incomplete"].map((f) => (
                    <button key={f} data-testid={`creators-filter-${f}`} onClick={() => setFilter(f)}
                        className={`yc-badge cursor-pointer transition-colors ${filter === f ? "bg-yc-ink text-white" : "bg-white"}`}>
                        {f === "all" ? `All (${creators.length})` : `${STATUS_META[f].label} (${counts[f] || 0})`}
                    </button>
                ))}
            </div>

            <div className="space-y-3">
                {shown.map((c) => (
                    <div key={c.creator_id} className="yc-card-sm bg-white" data-testid={`creator-row-${c.creator_id}`}>
                        <div className="p-4 flex flex-wrap items-center gap-3">
                            <div className="flex-1 min-w-[180px]">
                                <div className="font-heading font-bold">{c.name || "Unnamed"}</div>
                                <div className="text-xs text-yc-ink/60">{c.login_email} · joined {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</div>
                            </div>
                            <span className={`yc-badge text-[10px] ${STATUS_META[c.vetting_status]?.cls || "bg-white"}`}>{STATUS_META[c.vetting_status]?.label || c.vetting_status}</span>
                            <button data-testid={`creator-expand-${c.creator_id}`} onClick={() => setOpen(open === c.creator_id ? null : c.creator_id)} className="yc-btn-secondary text-xs py-1.5 px-3">
                                {open === c.creator_id ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />} Details
                            </button>
                            <button data-testid={`creator-remove-${c.creator_id}`} onClick={() => remove(c)} className="flex items-center gap-1.5 text-xs font-bold text-yc-pink border-2 border-yc-pink rounded-full px-3 py-1.5 hover:bg-yc-pink hover:text-white transition-colors">
                                <Trash2 className="w-3.5 h-3.5" /> Remove
                            </button>
                        </div>
                        {open === c.creator_id && (
                            <div className="border-t-2 border-yc-ink p-4 bg-yc-cream space-y-4" data-testid={`creator-details-${c.creator_id}`}>
                                <div className="flex justify-end">
                                    {editing === c.creator_id ? (
                                        <div className="flex gap-2">
                                            <button data-testid={`creator-save-${c.creator_id}`} disabled={savingEdit} onClick={() => saveEdit(c.creator_id)} className="yc-btn-primary text-xs py-1.5 px-3 disabled:opacity-50"><Save className="w-3.5 h-3.5 mr-1" /> Save</button>
                                            <button onClick={() => setEditing(null)} className="yc-btn-secondary text-xs py-1.5 px-3"><X className="w-3.5 h-3.5 mr-1" /> Cancel</button>
                                        </div>
                                    ) : (
                                        <button data-testid={`creator-edit-${c.creator_id}`} onClick={() => startEdit(c)} className="yc-btn-secondary text-xs py-1.5 px-3"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit account details</button>
                                    )}
                                </div>
                                {editing === c.creator_id ? (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid={`creator-edit-form-${c.creator_id}`}>
                                        {EDIT_FIELDS.map(([k, label]) => (
                                            <div key={k}>
                                                <div className="text-[10px] font-bold uppercase tracking-wider text-yc-ink/50">{label}</div>
                                                <input data-testid={`edit-${k}`} className="yc-input mt-1 text-sm" value={eform[k] || ""} onChange={(e) => setEform({ ...eform, [k]: e.target.value })} />
                                            </div>
                                        ))}
                                        <div className="md:col-span-3 text-xs text-yc-ink/50">Login email ({c.login_email}) can't be changed here — it's tied to their sign-in.</div>
                                    </div>
                                ) : (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                    {[
                                        ["First name", c.first_name], ["Surname", c.surname], ["Age", c.age],
                                        ["Telephone", c.telephone], ["Contact email", c.contact_email], ["Address", c.address],
                                        ["Account holder", c.bank_account_name], ["Sort code", c.bank_sort_code], ["Account number", c.bank_account_number],
                                        ["Location", c.location], ["Availability", c.availability],
                                        ["Agreement", c.agreement_accepted ? `Accepted ${c.agreement_version || ""} · ${c.agreement_accepted_at ? new Date(c.agreement_accepted_at).toLocaleDateString() : ""}` : "NOT accepted"],
                                        ["Quality score", c.quality_score], ["Reliability score", c.reliability_score],
                                        ["Vetting notes", c.vetting_notes],
                                    ].map(([l, v]) => (
                                        <div key={l}>
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-yc-ink/50">{l}</div>
                                            <div className="font-semibold break-words">{v || "—"}</div>
                                        </div>
                                    ))}
                                </div>
                                )}
                                <div className="flex flex-wrap gap-2 text-sm items-center">
                                    {[[Instagram, c.instagram_handle], [Music2, c.tiktok_handle], [Youtube, c.youtube_handle]].map(([Icon, v], i) => (
                                        <span key={`h-${i}`} className={`flex items-center gap-1.5 border-2 rounded-full px-3 py-1 text-xs ${v ? "border-yc-ink bg-white" : "border-dashed border-yc-ink/30 text-yc-ink/40"}`}>
                                            <Icon className="w-3.5 h-3.5" /> {v ? `@${v}` : "not provided"}
                                        </span>
                                    ))}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {(c.specialisms || []).map((s) => <span key={s} className="yc-badge bg-white text-[10px]">{specLabel(s)}</span>)}
                                </div>
                                {(c.vetting_status === "pending" || c.vetting_status === "more_info") && (
                                    <Link to="/admin/vetting" className="text-yc-blue text-sm font-bold underline underline-offset-4">Review in vetting queue →</Link>
                                )}
                            </div>
                        )}
                    </div>
                ))}
                {shown.length === 0 && <div className="yc-card p-8 text-center text-yc-ink/60">No creators match this filter.</div>}
            </div>
        </DashboardShell>
    );
};

export default AdminCreators;
